import { classNames } from '@/utility/className';
import React, { FC, ReactNode } from 'react';
import { BiErrorCircle } from 'react-icons/bi';
import {
  BsCheckCircleFill,
  BsFillExclamationCircleFill,
  BsInfoCircle,
} from 'react-icons/bs';
export type AlertProps = {
  title?: string;
  message?: ReactNode;
  actions?: ReactNode;
  dismissible?: boolean;
  onDismiss?: () => void;
  className?: string;
  iconClassName?: string;
  variant: 'success' | 'warning' | 'error' | 'info';
  list?: (string | { email: string })[];
};

const Alert: FC<AlertProps> = ({
  variant = 'info',
  className,
  iconClassName,
  actions,
  title,
  message,
  list,
  dismissible,
  onDismiss,
}: AlertProps) => {
  function AlertList({ items }: { items: (string | { email: string })[] }) {
    const [open, setOpen] = React.useState(false);
    const visible = open ? items : items.slice(0, 10);

    return (
      <>
        <ul className="list-disc list-inside space-y-0.5">
          {visible.map((it, i) => (
            <li key={i} className="break-all">
              {typeof it === 'string' ? it : it.email}
            </li>
          ))}
        </ul>
        {items.length > 10 && (
          <button
            type="button"
            className="underline text-xs mt-1"
            onClick={() => setOpen(!open)}
          >
            {open ? 'collapse' : `…show ${items.length - 10} more`}
          </button>
        )}
      </>
    );
  }

  const ariaRole = variant === 'error' ? 'alert' : 'status';
  const ariaLive = variant === 'error' ? 'assertive' : 'polite';

  return (
    <div
      role={ariaRole}
      aria-live={ariaLive}
      className={classNames(
        'rounded-md border border-opacity-20 p-2 mt-1',
        className,
        variant === 'error' && 'border-red-900 bg-red-50 text-red-800',
        variant === 'warning' &&
          'border-yellow-700 bg-yellow-50 text-yellow-700',
        variant === 'info' && 'border-sky-700 bg-sky-50 text-sky-700',
        variant === 'success' && 'border-green-700 bg-green-100 text-gray-900'
      )}
    >
      <div className="flex">
        <div className="flex-shrink-0">
          {variant === 'error' && (
            <BiErrorCircle
              className={classNames('h-5 w-5 text-red-400', iconClassName)}
              aria-hidden="true"
            />
          )}
          {variant === 'warning' && (
            <BsFillExclamationCircleFill
              className={classNames('h-5 w-5 text-yellow-400', iconClassName)}
              aria-hidden="true"
            />
          )}
          {variant === 'info' && (
            <BsInfoCircle
              className={classNames('h-5 w-5 text-sky-400', iconClassName)}
              aria-hidden="true"
            />
          )}
          {variant === 'success' && (
            <BsCheckCircleFill
              className={classNames('h-5 w-5 text-gray-700', iconClassName)}
              aria-hidden="true"
            />
          )}
        </div>
        <div className="ml-3 flex-grow">
          <h3 className="text-sm font-medium">{title}</h3>
          <div className="text-sm">{message}</div>
          {list && list.length > 0 && <AlertList items={list} />}
        </div>
        {(actions || dismissible) && (
          <div className="absolute top-1 right-1 flex items-center gap-2 text-sm md:relative">
            {actions}
            {dismissible && (
              <button
                type="button"
                aria-label="Dismiss"
                className="text-xs text-inherit underline"
                onClick={onDismiss}
              >
                Dismiss
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Alert;
